import React, { useState } from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, BarChart, Bar, ScatterChart, Scatter, ZAxis,
  ComposedChart, Area
} from 'recharts';
import { 
  TrendingUp, TrendingDown, AlertCircle, 
  Calendar, Clock, ArrowRight
} from 'lucide-react';
import type { GaitSession } from '../AdvancedGaitAnalysis';

interface LongitudinalAnalysisProps {
  sessions: GaitSession[];
  highlightedSession: GaitSession;
}

interface ThresholdCrossing {
  metric: string;
  threshold: string;
  date: string;
  message: string;
}

interface TrendData {
  date: Date;
  formattedDate: string;
  symmetryIndex: number;
  velocity: number;
  strideLength: number;
  riskScore: number;
  cadence: number;
  isHighlighted: boolean;
  [key: string]: any; // Allow for dynamically added trend properties
}

// Convert timestamp to date format for charts
const formatDate = (timestamp: number): string => {
  const date = new Date(timestamp);
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

const LongitudinalAnalysis: React.FC<LongitudinalAnalysisProps> = ({
  sessions,
  highlightedSession
}) => {
  const [timeRange, setTimeRange] = useState<'month' | 'week'>('month');
  const [metricToShow, setMetricToShow] = useState<'symmetry' | 'velocity' | 'risk'>('symmetry');
  
  // Sort sessions chronologically
  const sortedSessions = [...sessions].sort((a, b) => a.date.getTime() - b.date.getTime());
  
  // Extract dates and key metrics for trend chart
  const trendData: TrendData[] = sortedSessions.map(session => ({
    date: session.date,
    formattedDate: formatDate(session.date.getTime()),
    symmetryIndex: session.averageSymmetryIndex,
    velocity: session.averageVelocity,
    strideLength: session.averageStrideLength,
    riskScore: session.riskAssessment.score,
    cadence: session.averageCadence,
    isHighlighted: session.id === highlightedSession.id
  }));
  
  // Calculate the change in key metrics
  const getMetricChange = (metric: keyof TrendData) => {
    if (trendData.length < 2) return { value: 0, direction: 'no-change' as const };
    
    const firstValue = trendData[0][metric] as number;
    const lastValue = trendData[trendData.length - 1][metric] as number;
    const change = ((lastValue - firstValue) / firstValue) * 100;
    
    return {
      value: Math.abs(change),
      direction: change > 0 ? 'increase' as const : change < 0 ? 'decrease' as const : 'no-change' as const
    };
  };
  
  // Determine if a change is concerning or positive
  const isPositiveChange = (metric: string, direction: string): boolean => {
    if (metric === 'symmetryIndex' || metric === 'velocity' || metric === 'strideLength') {
      return direction === 'increase';
    }
    if (metric === 'riskScore') {
      return direction === 'decrease';
    }
    return false;
  };
  
  // Calculate trends
  const symmetryChange = getMetricChange('symmetryIndex');
  const velocityChange = getMetricChange('velocity');
  const riskChange = getMetricChange('riskScore');
  
  // Linear regression to calculate trend line
  const calculateTrendLine = (data: TrendData[], xKey: string, yKey: string): TrendData[] => {
    const n = data.length;
    if (n < 2) return data;
    
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumXX = 0;
    
    data.forEach((d, i) => {
      const x = i; // Use index as x value for simplicity
      const y = d[yKey] as number;
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumXX += x * x;
    });
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    
    return data.map((d, i) => ({
      ...d,
      [`${yKey}Trend`]: intercept + slope * i
    }));
  };
  
  // Apply trend lines to data
  const dataWithTrends = calculateTrendLine(
    calculateTrendLine(
      calculateTrendLine(trendData, 'date', 'symmetryIndex'),
      'date', 'velocity'
    ),
    'date', 'riskScore'
  );
  
  // Generate composite score data
  const compositeData = trendData.map(session => {
    // Normalize each metric from 0-100
    const normalizedSymmetry = session.symmetryIndex; // Already 0-100
    const normalizedVelocity = Math.min(100, (session.velocity / 120) * 100); // Normalize to max expected velocity
    const normalizedCadence = Math.min(100, (session.cadence / 120) * 100); // Normalize to max expected cadence
    
    // Higher is better for these metrics
    const compositeScore = (normalizedSymmetry * 0.4) + (normalizedVelocity * 0.3) + (normalizedCadence * 0.3);
    
    return {
      ...session,
      compositeScore
    };
  });
  
  // Calculate threshold crossings
  const findThresholdCrossings = (): ThresholdCrossing[] => {
    const crossings: ThresholdCrossing[] = [];
    
    // Check for symmetry falling below 80%
    for (let i = 1; i < trendData.length; i++) {
      if (trendData[i-1].symmetryIndex >= 80 && trendData[i].symmetryIndex < 80) {
        crossings.push({
          metric: 'Gait Symmetry',
          threshold: '80%',
          date: trendData[i].formattedDate,
          message: 'Fell below recommended symmetry threshold'
        });
      }
      
      if (trendData[i-1].velocity >= 3.0 && trendData[i].velocity < 3.0) {
        crossings.push({
          metric: 'Walking Speed',
          threshold: '3.0 ft/s',
          date: trendData[i].formattedDate,
          message: 'Decreased below typical walking speed'
        });
      }
      
      if (trendData[i-1].riskScore <= 60 && trendData[i].riskScore > 60) {
        crossings.push({
          metric: 'Fall Risk',
          threshold: '60 points',
          date: trendData[i].formattedDate,
          message: 'Exceeded moderate fall risk threshold'
        });
      }
    }
    
    return crossings;
  };
  
  const thresholdCrossings = findThresholdCrossings();
  
  // Render top summary stats
  const renderSummaryStats = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      <div className={`bg-white rounded-lg shadow p-4 border-l-4 ${
        isPositiveChange('symmetryIndex', symmetryChange.direction)
          ? 'border-green-500' : 'border-red-500'
      }`}>
        <div className="flex justify-between items-start">
          <div>
            <div className="text-gray-500 mb-1">Gait Symmetry</div>
            <div className="text-2xl font-bold mb-1">
              {trendData[trendData.length - 1].symmetryIndex.toFixed(1)}%
            </div>
          </div>
          <div className={`flex items-center ${
            isPositiveChange('symmetryIndex', symmetryChange.direction)
              ? 'text-green-600' : 'text-red-600'
          }`}>
            {symmetryChange.direction === 'increase' ? (
              <TrendingUp size={20} className="mr-1" />
            ) : symmetryChange.direction === 'decrease' ? (
              <TrendingDown size={20} className="mr-1" />
            ) : (
              <ArrowRight size={20} className="mr-1 text-gray-500" />
            )}
            <span>{symmetryChange.value.toFixed(1)}%</span>
          </div>
        </div>
        <div className="text-sm text-gray-600 mt-2">
          {isPositiveChange('symmetryIndex', symmetryChange.direction) 
            ? 'Improving trend in gait symmetry'
            : 'Declining trend in gait symmetry'}
        </div>
      </div>
      
      <div className={`bg-white rounded-lg shadow p-4 border-l-4 ${
        isPositiveChange('velocity', velocityChange.direction)
          ? 'border-green-500' : 'border-red-500'
      }`}>
        <div className="flex justify-between items-start">
          <div>
            <div className="text-gray-500 mb-1">Walking Speed</div>
            <div className="text-2xl font-bold mb-1">
              {trendData[trendData.length - 1].velocity.toFixed(1)} ft/s
            </div>
          </div>
          <div className={`flex items-center ${
            isPositiveChange('velocity', velocityChange.direction)
              ? 'text-green-600' : 'text-red-600'
          }`}>
            {velocityChange.direction === 'increase' ? (
              <TrendingUp size={20} className="mr-1" />
            ) : velocityChange.direction === 'decrease' ? (
              <TrendingDown size={20} className="mr-1" />
            ) : (
              <ArrowRight size={20} className="mr-1 text-gray-500" />
            )}
            <span>{velocityChange.value.toFixed(1)}%</span>
          </div>
        </div>
        <div className="text-sm text-gray-600 mt-2">
          {isPositiveChange('velocity', velocityChange.direction) 
            ? 'Improving trend in walking speed'
            : 'Declining trend in walking speed'}
        </div>
      </div>
      
      <div className={`bg-white rounded-lg shadow p-4 border-l-4 ${
        isPositiveChange('riskScore', riskChange.direction)
          ? 'border-green-500' : 'border-red-500'
      }`}>
        <div className="flex justify-between items-start">
          <div>
            <div className="text-gray-500 mb-1">Fall Risk Score</div>
            <div className="text-2xl font-bold mb-1">
              {trendData[trendData.length - 1].riskScore.toFixed(0)}
            </div>
          </div>
          <div className={`flex items-center ${
            isPositiveChange('riskScore', riskChange.direction)
              ? 'text-green-600' : 'text-red-600'
          }`}>
            {riskChange.direction === 'increase' ? (
              <TrendingUp size={20} className="mr-1" />
            ) : riskChange.direction === 'decrease' ? (
              <TrendingDown size={20} className="mr-1" />
            ) : (
              <ArrowRight size={20} className="mr-1 text-gray-500" />
            )}
            <span>{riskChange.value.toFixed(1)}%</span>
          </div>
        </div>
        <div className="text-sm text-gray-600 mt-2">
          {isPositiveChange('riskScore', riskChange.direction) 
            ? 'Improving trend in fall risk'
            : 'Increasing fall risk detected'}
        </div>
      </div>
    </div>
  );
  
  // Create custom dot renderer for highlighting specific points
  const renderHighlightedDot = (color: string) => (props: any) => {
    const { cx, cy, payload } = props;
    return payload.isHighlighted ? (
      <circle cx={cx} cy={cy} r={6} stroke={color} strokeWidth={2} fill={color} fillOpacity={0.5} />
    ) : (
      <circle cx={cx} cy={cy} r={4} stroke={color} strokeWidth={2} fill="white" fillOpacity={0} />
    );
  };
  
  // Render metric trend chart
  const renderTrendChart = () => (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="p-4 border-b flex justify-between items-center">
        <h3 className="font-medium">Longitudinal Trends</h3>
        <div className="flex space-x-2">
          <div className="flex space-x-2">
            <button
              onClick={() => setMetricToShow('symmetry')}
              className={`px-3 py-1 text-sm rounded ${
                metricToShow === 'symmetry' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Symmetry
            </button>
            <button
              onClick={() => setMetricToShow('velocity')}
              className={`px-3 py-1 text-sm rounded ${
                metricToShow === 'velocity' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Velocity
            </button>
            <button
              onClick={() => setMetricToShow('risk')}
              className={`px-3 py-1 text-sm rounded ${
                metricToShow === 'risk' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Risk
            </button>
          </div>
        </div>
      </div>
      <div className="p-4">
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={dataWithTrends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="formattedDate" 
              label={{ value: 'Date', position: 'insideBottomRight', offset: -10 }} 
            />
            <YAxis 
              label={{ 
                value: metricToShow === 'symmetry' 
                  ? 'Symmetry Index (%)' 
                  : metricToShow === 'velocity'
                  ? 'Walking Speed (ft/s)'
                  : 'Risk Score',
                angle: -90, 
                position: 'insideLeft' 
              }} 
            />
            <Tooltip 
              formatter={(value, name) => {
                if (typeof name === 'string' && name.includes('Trend')) return [`${Number(value).toFixed(1)}`, 'Trend Line'];
                return [`${Number(value).toFixed(1)}`, name];
              }}
            />
            <Legend />

            {metricToShow === 'symmetry' && (
              <>
                <Line 
                  type="monotone" 
                  dataKey="symmetryIndex" 
                  name="Gait Symmetry" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  dot={renderHighlightedDot('#3b82f6')}
                  activeDot={{ r: 8, fill: '#3b82f6' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="symmetryIndexTrend" 
                  name="Trend" 
                  stroke="#9ca3af" 
                  strokeDasharray="5 5"
                  dot={false}
                />
                <Area
                  type="monotone"
                  dataKey="symmetryIndex"
                  fill="#3b82f6"
                  fillOpacity={0.1}
                  stroke="none"
                />
              </>
            )}

            {metricToShow === 'velocity' && (
              <>
                <Line 
                  type="monotone" 
                  dataKey="velocity" 
                  name="Walking Speed" 
                  stroke="#f97316" 
                  strokeWidth={2}
                  dot={renderHighlightedDot('#f97316')}
                  activeDot={{ r: 8, fill: '#f97316' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="velocityTrend" 
                  name="Trend" 
                  stroke="#9ca3af" 
                  strokeDasharray="5 5"
                  dot={false}
                />
                <Area
                  type="monotone"
                  dataKey="velocity"
                  fill="#f97316"
                  fillOpacity={0.1}
                  stroke="none"
                />
              </>
            )}

            {metricToShow === 'risk' && (
              <>
                <Line 
                  type="monotone" 
                  dataKey="riskScore" 
                  name="Fall Risk" 
                  stroke="#ef4444" 
                  strokeWidth={2}
                  dot={renderHighlightedDot('#ef4444')}
                  activeDot={{ r: 8, fill: '#ef4444' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="riskScoreTrend" 
                  name="Trend" 
                  stroke="#9ca3af" 
                  strokeDasharray="5 5"
                  dot={false}
                />
                <Area
                  type="monotone"
                  dataKey="riskScore"
                  fill="#ef4444"
                  fillOpacity={0.1}
                  stroke="none"
                />
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
  
  // Render multi-factor chart
  const renderMultiFactorChart = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="font-medium">Composite Health Score</h3>
        </div>
        <div className="p-4">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={compositeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="formattedDate" />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="compositeScore" 
                name="Overall Score" 
                stroke="#8884d8" 
                strokeWidth={2}
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="text-sm text-gray-600 mt-2 text-center">
            Composite score based on gait symmetry, walking speed, and cadence
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="font-medium">Multi-Parameter Correlation</h3>
        </div>
        <div className="p-4">
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <CartesianGrid />
              <XAxis 
                type="number" 
                dataKey="velocity" 
                name="Walking Speed" 
                unit=" ft/s" 
                domain={[60, 120]}
              />
              <YAxis 
                type="number" 
                dataKey="symmetryIndex" 
                name="Symmetry Index" 
                unit="%" 
                domain={[60, 100]}
              />
              <ZAxis 
                type="number" 
                dataKey="riskScore" 
                range={[50, 400]} 
                name="Risk Score" 
              />
              <Tooltip 
                cursor={{ strokeDasharray: '3 3' }}
                formatter={(value, name) => {
                  if (typeof name === 'string') {
                    return [`${Number(value).toFixed(1)}${name.includes('Speed') ? ' ft/s' : name.includes('Index') ? '%' : ''}`, name];
                  }
                  return [`${Number(value).toFixed(1)}`, name];
                }}
              />
              <Legend />
              <Scatter 
                name="Sessions" 
                data={trendData} 
                fill="#8884d8"
                shape={(props: any) => {
                  const { cx, cy, payload } = props;
                  return payload.isHighlighted ? (
                    <circle cx={cx} cy={cy} r={8} stroke="#8884d8" strokeWidth={2} fill="#8884d8" fillOpacity={0.5} />
                  ) : (
                    <circle cx={cx} cy={cy} r={6} fill="#8884d8" fillOpacity={0.5} />
                  );
                }}
              />
            </ScatterChart>
          </ResponsiveContainer>
          <div className="text-sm text-gray-600 mt-2 text-center">
            Bubble size represents risk score (larger = higher risk)
          </div>
        </div>
      </div>
    </div>
  );
  
  // Render threshold crossings and insights
  const renderThresholdAndInsights = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="font-medium">Threshold Crossings</h3>
        </div>
        <div className="p-4">
          {thresholdCrossings.length > 0 ? (
            <div className="space-y-4">
              {thresholdCrossings.map((crossing, index) => (
                <div key={index} className="border rounded-lg p-3 bg-red-50 border-red-200">
                  <div className="flex items-start">
                    <AlertCircle className="text-red-500 mr-2 flex-shrink-0 mt-1" size={18} />
                    <div>
                      <div className="font-medium">{crossing.metric} crossed {crossing.threshold} threshold</div>
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Date:</span> {crossing.date}
                      </div>
                      <div className="text-sm text-gray-700 mt-1">
                        {crossing.message}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-6">
              No threshold crossings detected in this time period
            </div>
          )}
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="font-medium">Analysis Insights</h3>
        </div>
        <div className="p-4">
          <div className="space-y-4">
            <div className="flex items-start">
              <TrendingUp className="text-blue-500 mr-2 flex-shrink-0 mt-1" size={18} />
              <div>
                <div className="font-medium">Gait Pattern Trend</div>
                <div className="text-sm text-gray-700 mt-1">
                  {symmetryChange.direction === 'decrease' && symmetryChange.value > 5 ? (
                    <>
                      Gait symmetry has declined by {symmetryChange.value.toFixed(1)}% over the monitored period, 
                      suggesting progressive changes in movement patterns.
                    </>
                  ) : velocityChange.direction === 'decrease' && velocityChange.value > 5 ? (
                    <>
                      Walking speed has decreased by {velocityChange.value.toFixed(1)}% over the monitored period, 
                      which may indicate reduced mobility or confidence.
                    </>
                  ) : riskChange.direction === 'increase' && riskChange.value > 5 ? (
                    <>
                      Fall risk score has increased by {riskChange.value.toFixed(1)}% over the monitored period,
                      suggesting attention to fall prevention may be needed.
                    </>
                  ) : (
                    <>
                      Gait patterns remain relatively stable over the monitoring period with 
                      only minor fluctuations in key metrics.
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-start">
              <Calendar className="text-purple-500 mr-2 flex-shrink-0 mt-1" size={18} />
              <div>
                <div className="font-medium">Periodic Patterns</div>
                <div className="text-sm text-gray-700 mt-1">
                  {/* This would be based on real pattern detection in a production system */}
                  Analysis shows slight but noticeable weekly patterns, with better performance 
                  typically observed during midweek days and reduced metrics on weekends.
                </div>
              </div>
            </div>
            
            <div className="flex items-start">
              <Clock className="text-green-500 mr-2 flex-shrink-0 mt-1" size={18} />
              <div>
                <div className="font-medium">Progression Rate</div>
                <div className="text-sm text-gray-700 mt-1">
                  Current rate of change suggests monitoring should continue at the current frequency.
                  {riskChange.direction === 'increase' && riskChange.value > 10 ? (
                    <span className="text-red-600 font-medium"> Consider increasing monitoring frequency based on risk increase.</span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  
  return (
    <div className="p-6">
      {renderSummaryStats()}
      {renderTrendChart()}
      {renderMultiFactorChart()}
      {renderThresholdAndInsights()}
    </div>
  );
};

export default LongitudinalAnalysis; 